import React from 'react';

const MLExamples = () => {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(550px, 1fr))',
      gap: '40px',
      marginTop: '30px'
    }}>
      {/* Approach 1: Single Agent Example */}
      <div style={{
        background: 'rgba(255, 107, 107, 0.1)',
        padding: '30px',
        borderRadius: '16px',
        border: '1px solid rgba(255, 107, 107, 0.3)'
      }}>
        <h4 style={{ 
          margin: '0 0 20px 0', 
          fontSize: '20px',
          color: '#FF6B6B',
          display: 'flex',
          alignItems: 'center',
          gap: '10px'
        }}>
          🤖 Single Agent Approach
        </h4>
        
        <div style={{
          background: 'rgba(0, 0, 0, 0.3)',
          padding: '20px',
          borderRadius: '10px',
          fontFamily: 'monospace',
          fontSize: '14px'
        }}>
          <div style={{ marginBottom: '15px', color: '#FF6B6B' }}>
            <strong>User:</strong> "Improve my model's accuracy"
          </div>
          <div style={{ marginBottom: '10px', fontSize: '12px', opacity: 0.8 }}>
            <em>Context: Mortgage default prediction model (Random Forest)</em>
          </div>
          
          <div style={{ marginBottom: '20px' }}>
            <strong style={{ color: '#FF9999' }}>Agent's Single Solution:</strong>
          </div>
          
          <div style={{
            background: 'rgba(255, 255, 255, 0.05)',
            padding: '15px',
            borderRadius: '8px',
            fontSize: '13px',
            lineHeight: '1.8'
          }}>
            <div style={{ marginBottom: '10px' }}>
              • Increased trees from 100 to 500
            </div>
            <div style={{ marginBottom: '10px' }}>
              • Added GridSearchCV for hyperparameters
            </div>
            <div style={{ marginBottom: '10px' }}>
              • Basic feature scaling with StandardScaler
            </div>
            <div style={{ 
              marginTop: '15px',
              paddingTop: '15px',
              borderTop: '1px solid rgba(255, 255, 255, 0.1)',
              fontSize: '16px',
              fontWeight: '600'
            }}>
              <strong>Result:</strong> 
              <div style={{ marginTop: '8px', fontSize: '14px' }}>
                • Accuracy: <span style={{ color: '#FF6B6B' }}>82% → 84%</span> (+2%)
                <br/>• F1-Score: <span style={{ color: '#FF6B6B' }}>0.78 → 0.80</span>
              </div>
            </div>
          </div>
          
          <div style={{
            marginTop: '20px',
            padding: '15px',
            background: 'rgba(255, 107, 107, 0.1)',
            borderRadius: '8px',
            fontSize: '13px'
          }}>
            <strong>❌ Missed Opportunities:</strong>
            <ul style={{ margin: '8px 0 0 20px', paddingLeft: '0' }}>
              <li>No feature engineering or domain-specific features</li>
              <li>Didn't address class imbalance (95% non-defaults)</li>
              <li>No ensemble methods or model stacking</li>
              <li>Ignored temporal patterns in mortgage data</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Approach 2: Current Artemis Example */}
      <div style={{
        background: 'rgba(78, 205, 196, 0.1)',
        padding: '30px',
        borderRadius: '16px',
        border: '1px solid rgba(78, 205, 196, 0.3)'
      }}>
        <h4 style={{ 
          margin: '0 0 20px 0', 
          fontSize: '20px',
          color: '#4ECDC4',
          display: 'flex',
          alignItems: 'center',
          gap: '10px'
        }}>
          🚀 Current Artemis Approach
        </h4>
        
        <div style={{
          background: 'rgba(0, 0, 0, 0.3)',
          padding: '20px',
          borderRadius: '10px',
          fontFamily: 'monospace',
          fontSize: '14px'
        }}>
          <div style={{ marginBottom: '15px', color: '#4ECDC4' }}>
            <strong>User:</strong> "Improve my model's accuracy"
          </div>
          <div style={{ marginBottom: '10px', fontSize: '12px', opacity: 0.8 }}>
            <em>Context: Mortgage default prediction model (Random Forest)</em>
          </div>
          
          <div style={{ marginBottom: '20px' }}>
            <strong style={{ color: '#96CEB4' }}>Artemis Evolution Process:</strong>
          </div>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {[
              {
                branch: 'evolution-1',
                approach: 'Feature Engineering Focus',
                changes: [
                  'Debt-to-income ratio buckets',
                  'Payment history patterns',
                  'Economic indicators integration'
                ],
                metrics: { accuracy: '86%', f1: '0.83' },
                color: '#FF6B6B'
              },
              {
                branch: 'evolution-2',
                approach: 'Class Imbalance Handling',
                changes: [
                  'SMOTE for synthetic samples',
                  'Class weight balancing',
                  'Focal loss implementation'
                ],
                metrics: { accuracy: '85%', f1: '0.85' },
                color: '#96CEB4'
              },
              {
                branch: 'evolution-3',
                approach: 'Advanced Modeling',
                changes: [
                  'XGBoost with custom objective',
                  'Neural network ensemble',
                  'Time-series features'
                ],
                metrics: { accuracy: '88%', f1: '0.86' },
                color: '#DDA0DD'
              },
              {
                branch: 'evolution-4',
                approach: 'Hybrid Best Solution',
                changes: [
                  'Stacked ensemble (RF + XGB + NN)',
                  'Engineered features + SMOTE',
                  'Calibrated probability outputs'
                ],
                metrics: { accuracy: '91%', f1: '0.89' },
                color: '#4ECDC4',
                best: true
              }
            ].map((evolution, i) => (
              <div key={i} style={{
                background: evolution.best 
                  ? `linear-gradient(135deg, ${evolution.color}20, ${evolution.color}10)`
                  : 'rgba(255, 255, 255, 0.05)',
                padding: '15px',
                borderRadius: '8px',
                border: evolution.best 
                  ? `2px solid ${evolution.color}60`
                  : '1px solid rgba(255, 255, 255, 0.1)',
                fontSize: '13px'
              }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '10px'
                }}>
                  <span style={{ 
                    color: evolution.color,
                    fontWeight: '600'
                  }}>
                    {evolution.branch}: {evolution.approach}
                  </span>
                  <div style={{ display: 'flex', gap: '10px', fontSize: '12px' }}>
                    <span style={{
                      background: 'rgba(255, 255, 255, 0.1)',
                      padding: '3px 8px',
                      borderRadius: '8px'
                    }}>
                      Acc: {evolution.metrics.accuracy}
                    </span>
                    <span style={{
                      background: evolution.best ? evolution.color : 'rgba(255, 255, 255, 0.1)',
                      color: evolution.best ? 'white' : evolution.color,
                      padding: '3px 8px',
                      borderRadius: '8px',
                      fontWeight: '600'
                    }}>
                      F1: {evolution.metrics.f1}
                    </span>
                  </div>
                </div>
                <ul style={{ margin: '0', paddingLeft: '20px', opacity: 0.9 }}>
                  {evolution.changes.map((change, idx) => (
                    <li key={idx} style={{ marginBottom: '3px' }}>{change}</li>
                  ))}
                </ul>
                {evolution.best && (
                  <div style={{
                    marginTop: '10px',
                    paddingTop: '10px',
                    borderTop: '1px solid rgba(78, 205, 196, 0.3)',
                    color: '#4ECDC4',
                    fontWeight: '600',
                    fontSize: '12px'
                  }}>
                    ✅ SELECTED - Best balance of metrics
                  </div>
                )}
              </div>
            ))}
          </div>
          
          <div style={{
            marginTop: '20px',
            padding: '15px',
            background: 'rgba(78, 205, 196, 0.1)',
            borderRadius: '8px',
            fontSize: '13px'
          }}>
            <strong>✅ Artemis Advantages:</strong>
            <ul style={{ margin: '8px 0 0 20px', paddingLeft: '0' }}>
              <li>Explored multiple improvement strategies</li>
              <li>Addressed class imbalance properly</li>
              <li>Combined best techniques from all approaches</li>
              <li>Achieved <strong style={{ color: '#4ECDC4' }}>91% accuracy</strong> (+9% from baseline)</li>
              <li>F1-Score: <strong style={{ color: '#4ECDC4' }}>0.89</strong> (critical for imbalanced data)</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Approach 3: Multi-Agent Example */}
      <div style={{
        background: 'rgba(244, 81, 30, 0.1)',
        padding: '30px',
        borderRadius: '16px',
        border: '2px solid rgba(244, 81, 30, 0.3)',
        gridColumn: 'span 2'
      }}>
        <h4 style={{ 
          margin: '0 0 20px 0', 
          fontSize: '20px',
          color: '#F4511E',
          display: 'flex',
          alignItems: 'center',
          gap: '10px'
        }}>
          🔥 Multi-Agent Artemis Approach
          <span style={{
            background: 'rgba(244, 81, 30, 0.2)',
            padding: '4px 12px',
            borderRadius: '12px',
            fontSize: '14px',
            fontWeight: '600'
          }}>
            Projected
          </span>
        </h4>
        
        <div style={{
          background: 'rgba(0, 0, 0, 0.3)',
          padding: '20px',
          borderRadius: '10px',
          fontFamily: 'monospace',
          fontSize: '14px'
        }}>
          <div style={{ marginBottom: '15px', color: '#F4511E' }}>
            <strong>User:</strong> "Improve my model's accuracy"
          </div>
          <div style={{ marginBottom: '10px', fontSize: '12px', opacity: 0.8 }}>
            <em>Context: Mortgage default prediction model (Random Forest)</em>
          </div>
          
          <div style={{ marginBottom: '20px' }}>
            <strong style={{ color: '#FF6D00' }}>Multi-Agent Competition & Evolution:</strong>
          </div>
          
          {/* Round 1: Initial approaches */}
          <div style={{
            background: 'rgba(255, 255, 255, 0.05)',
            padding: '20px',
            borderRadius: '10px',
            marginBottom: '20px'
          }}>
            <div style={{ fontSize: '15px', fontWeight: '600', marginBottom: '15px', color: '#F4511E' }}>
              Round 1: Independent Agent Solutions
            </div>
            
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '12px'
            }}>
              {[
                {
                  agent: 'GPT-4',
                  icon: '🤖',
                  approach: 'Deep learning with attention',
                  techniques: [
                    'Transformer architecture',
                    'Self-attention on sequences',
                    'Pre-trained embeddings'
                  ],
                  metrics: { acc: '89%', f1: '0.87' },
                  color: '#FF6B6B'
                },
                {
                  agent: 'Claude',
                  icon: '🧠',
                  approach: 'Domain-driven feature engineering',
                  techniques: [
                    'Mortgage industry features',
                    'Economic cycle indicators',
                    'Behavioral patterns'
                  ],
                  metrics: { acc: '90%', f1: '0.88' },
                  color: '#4ECDC4'
                },
                {
                  agent: 'Gemini',
                  icon: '⚡',
                  approach: 'Advanced ensemble methods',
                  techniques: [
                    'Stacking with meta-learner',
                    'Blending strategies',
                    'Dynamic weighting'
                  ],
                  metrics: { acc: '91%', f1: '0.89' },
                  color: '#96CEB4'
                },
                {
                  agent: 'Llama',
                  icon: '🦙',
                  approach: 'Interpretable ML focus',
                  techniques: [
                    'SHAP explanations',
                    'Monotonic constraints',
                    'Rule extraction'
                  ],
                  metrics: { acc: '88%', f1: '0.90' },
                  color: '#DDA0DD'
                }
              ].map((agent, i) => (
                <div key={i} style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '10px',
                  padding: '15px',
                  background: `${agent.color}15`,
                  borderRadius: '8px',
                  border: `1px solid ${agent.color}30`,
                  fontSize: '13px'
                }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '18px' }}>{agent.icon}</span>
                      <span style={{ 
                        color: agent.color, 
                        fontWeight: '600'
                      }}>
                        {agent.agent}
                      </span>
                    </div>
                    <div style={{ 
                      fontSize: '11px',
                      display: 'flex',
                      gap: '6px'
                    }}>
                      <span style={{
                        background: 'rgba(255, 255, 255, 0.1)',
                        padding: '2px 6px',
                        borderRadius: '6px'
                      }}>
                        {agent.metrics.acc}
                      </span>
                      <span style={{
                        background: agent.color,
                        color: 'white',
                        padding: '2px 6px',
                        borderRadius: '6px',
                        fontWeight: '600'
                      }}>
                        F1: {agent.metrics.f1}
                      </span>
                    </div>
                  </div>
                  <div style={{ fontSize: '12px', fontWeight: '500' }}>
                    {agent.approach}
                  </div>
                  <div style={{ 
                    fontSize: '11px', 
                    opacity: 0.8,
                    display: 'flex',
                    gap: '6px',
                    flexWrap: 'wrap'
                  }}>
                    {agent.techniques.map((tech, idx) => (
                      <span key={idx} style={{
                        background: 'rgba(255, 255, 255, 0.1)',
                        padding: '3px 8px',
                        borderRadius: '4px'
                      }}>
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Round 2: Cross-pollination */}
          <div style={{
            background: 'rgba(244, 81, 30, 0.1)',
            padding: '20px',
            borderRadius: '10px',
            marginBottom: '20px',
            border: '1px solid rgba(244, 81, 30, 0.3)'
          }}>
            <div style={{ fontSize: '15px', fontWeight: '600', marginBottom: '15px', color: '#F4511E' }}>
              Round 2: Artemis-Guided Cross-Pollination
            </div>
            
            <div style={{ fontSize: '13px', lineHeight: '1.8', marginBottom: '15px' }}>
              <strong>Artemis Feedback:</strong>
              <ul style={{ margin: '8px 0 0 20px', paddingLeft: '0' }}>
                <li>Combine Claude's domain features with GPT-4's deep learning</li>
                <li>Apply Llama's interpretability to Gemini's ensembles</li>
                <li>Create hybrid architectures leveraging all strengths</li>
              </ul>
            </div>
            
            <div style={{
              background: 'rgba(255, 255, 255, 0.05)',
              padding: '15px',
              borderRadius: '8px',
              fontSize: '13px'
            }}>
              <strong>New Hybrid Approaches:</strong>
              <div style={{ marginTop: '10px', lineHeight: '1.8' }}>
                • GPT-4 + Claude: Domain-aware transformers → <span style={{ color: '#4ECDC4', fontWeight: '600' }}>93% acc, 0.91 F1</span><br/>
                • Gemini + Llama: Interpretable ensembles → <span style={{ color: '#96CEB4', fontWeight: '600' }}>92% acc, 0.92 F1</span>
              </div>
            </div>
          </div>

          {/* Round 3: Final evolution */}
          <div style={{
            background: 'linear-gradient(135deg, rgba(244, 81, 30, 0.2), rgba(255, 109, 0, 0.1))',
            padding: '20px',
            borderRadius: '10px',
            border: '2px solid rgba(244, 81, 30, 0.5)'
          }}>
            <div style={{ fontSize: '15px', fontWeight: '600', marginBottom: '15px', color: '#F4511E' }}>
              Round 3: Final Evolution - Best of All Worlds
            </div>
            
            <div style={{
              background: 'rgba(0, 0, 0, 0.3)',
              padding: '15px',
              borderRadius: '8px',
              fontSize: '13px',
              lineHeight: '1.8'
            }}>
              <strong style={{ color: '#FF6D00' }}>Ultimate Solution:</strong>
              <ul style={{ margin: '10px 0 0 20px', paddingLeft: '0' }}>
                <li>Hierarchical ensemble with domain-aware transformers</li>
                <li>Engineered features feeding into deep learning layers</li>
                <li>Interpretable meta-learner for final predictions</li>
                <li>Temporal validation with economic cycle awareness</li>
                <li>Uncertainty quantification for risk assessment</li>
                <li>Automated feature discovery through neural architecture search</li>
              </ul>
              
              <div style={{
                marginTop: '15px',
                paddingTop: '15px',
                borderTop: '1px solid rgba(255, 255, 255, 0.2)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <span>
                  <strong>Projected Result:</strong>
                </span>
                <div style={{ textAlign: 'right' }}>
                  <div style={{
                    background: 'linear-gradient(135deg, #F4511E, #FF6D00)',
                    color: 'white',
                    padding: '8px 20px',
                    borderRadius: '20px',
                    fontWeight: '700',
                    fontSize: '16px',
                    marginBottom: '8px'
                  }}>
                    95% accuracy
                  </div>
                  <div style={{ fontSize: '14px', opacity: 0.9 }}>
                    F1-Score: <strong>0.94</strong> | AUC: <strong>0.98</strong>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div style={{
            marginTop: '20px',
            padding: '15px',
            background: 'rgba(244, 81, 30, 0.1)',
            borderRadius: '8px',
            fontSize: '13px'
          }}>
            <strong>🚀 Multi-Agent Advantages:</strong>
            <ul style={{ margin: '8px 0 0 20px', paddingLeft: '0' }}>
              <li>Each agent brings unique ML perspective (deep learning, domain knowledge, ensembles, interpretability)</li>
              <li>Discovers novel architectures through cross-pollination</li>
              <li>Balances accuracy with interpretability requirements</li>
              <li>Projected <strong style={{ color: '#F4511E' }}>95% accuracy</strong> vs 91% from single approach</li>
              <li>Regulatory-compliant with full explainability</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MLExamples; 